import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, PLATFORM_ID, computed, inject, signal} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ServiceApi} from '../../../../network/service.api';

interface PricePoint {
  time: number;
  value: number;
}

interface ChartData {
  buyPrice: number;
  sellPrice: number;
  currentTime: number;
  points: PricePoint[];
  /** میانگین آخرین روزِ تمام‌شده؛ مبنای «تغییرات امروز» */
  previousAverage?: number;
}

const DAY = 86_400_000;

// هندسه‌ی نمودار عیناً از طرح
const W = 720;
const H = 320;
const PAD = 8;
const PLOT_H = H - PAD * 2 - 28;

const TZ = 'Asia/Tehran';
const fullDate = new Intl.DateTimeFormat('fa-IR', {timeZone: TZ, day: 'numeric', month: 'long', year: 'numeric'});
const dayMonth = new Intl.DateTimeFormat('fa-IR', {timeZone: TZ, day: 'numeric', month: 'long'});
const monthName = new Intl.DateTimeFormat('fa-IR', {timeZone: TZ, month: 'long'});
const shortYear = new Intl.DateTimeFormat('fa-IR', {timeZone: TZ, year: '2-digit'});

const toRial = (value: number) => Math.round(value).toLocaleString('fa-IR');

/**
 * کارت قیمت لحظه‌ای و نمودار هیروی صفحه‌ی اصلی.
 * همه‌ی اعداد از سرویس قیمت می‌آیند؛ آخرین نقطه‌ی نمودار همان قیمت خرید لحظه‌ای است.
 */
@Component({
  selector: 'app-price-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './price-card.component.html',
  styleUrl: './price-card.component.scss'
})
export class PriceCardComponent implements OnInit {

  private readonly api = inject(ServiceApi);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly ranges = [
    {days: 7, label: 'هفته', title: 'هفته‌ی گذشته'},
    {days: 30, label: 'ماه', title: 'ماه گذشته'},
    {days: 90, label: 'فصل', title: 'سه ماه گذشته'},
    {days: 365, label: 'سال', title: 'سال گذشته'}
  ];

  readonly gridYs = [0, .25, .5, .75, 1].map(g => PAD + g * PLOT_H);
  readonly gridX1 = PAD;
  readonly gridX2 = W - PAD;

  readonly range = signal(30);
  readonly data = signal<ChartData | null>(null);
  readonly loading = signal(false);
  readonly hoverIndex = signal<number | null>(null);
  /** موقعیت راهنما به پیکسل و این‌که به کدام لبه چسبیده */
  readonly tip = signal<{ left: number; top: number; edge: 'start' | 'center' | 'end' } | null>(null);

  private readonly cache = new Map<number, ChartData>();

  readonly buyLabel = computed(() => this.data() ? toRial(this.data()!.buyPrice) : '—');
  readonly sellLabel = computed(() => this.data() ? toRial(this.data()!.sellPrice) : '—');
  readonly dateLabel = computed(() => this.data() ? fullDate.format(this.data()!.currentTime) : '');

  readonly geometry = computed(() => {
    const data = this.data();
    if (!data || data.points.length < 2) return null;

    const values = data.points.map(p => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const last = data.points.length - 1;

    const coords = data.points.map((p, i) => ({
      x: PAD + i / last * (W - PAD * 2),
      y: PAD + (1 - (p.value - min) / span) * PLOT_H
    }));

    const line = coords.map((c, i) => (i ? 'L' : 'M') + c.x.toFixed(1) + ' ' + c.y.toFixed(1)).join(' ');
    const area = `${line} L${coords[last].x.toFixed(1)} ${H - 20} L${coords[0].x.toFixed(1)} ${H - 20} Z`;

    return {
      // با عوض شدن کلید، مسیر دوباره ساخته می‌شود و انیمیشن کشیده شدن خط از نو پخش می‌شود
      key: `${this.range()}-${data.currentTime}`,
      line,
      area,
      coords,
      end: coords[last],
      min,
      max
    };
  });

  /** برای @for — فقط برای این‌که با عوض شدن کلید، عنصر مسیر از نو ساخته شود */
  readonly drawn = computed(() => {
    const g = this.geometry();
    return g ? [g] : [];
  });

  readonly hover = computed(() => {
    const i = this.hoverIndex();
    const g = this.geometry();
    const data = this.data();
    if (i === null || !g || !data) return null;
    const isLive = i === data.points.length - 1;
    const point = data.points[i];
    return {
      x: g.coords[i].x,
      y: g.coords[i].y,
      price: toRial(point.value),
      when: isLive ? 'امروز · قیمت لحظه‌ای' : `${fullDate.format(point.time)} · میانگین روز`
    };
  });

  readonly change = computed(() => {
    const data = this.data();
    if (!data?.previousAverage) return null;
    const pct = (data.buyPrice - data.previousAverage) / data.previousAverage * 100;
    const digits = Math.abs(pct).toLocaleString('fa-IR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return {up: pct >= 0, label: `${pct >= 0 ? '▲' : '▼'} ${digits}٪`};
  });

  readonly highLabel = computed(() => this.geometry() ? toRial(this.geometry()!.max) : '—');
  readonly lowLabel = computed(() => this.geometry() ? toRial(this.geometry()!.min) : '—');

  /** چهار برچسب محور، از قدیمی به جدید (محور چپ‌به‌راست است، مثل خود نمودار) */
  readonly axis = computed(() => {
    const data = this.data();
    if (!data) return [];
    const points = data.points;
    const last = points.length - 1;
    const yearly = this.range() > 180;
    return [0, 1 / 3, 2 / 3, 1].map((f, n) => {
      const point = points[Math.round(f * last)];
      if (n === 3) return 'امروز';
      if (!yearly) return dayMonth.format(point.time);
      // مثل طرح: سال فقط کنار ماهِ اولِ بازه
      return n === 0 ? `${monthName.format(point.time)} ${shortYear.format(point.time)}` : monthName.format(point.time);
    });
  });

  readonly chartLabel = computed(() => {
    const r = this.ranges.find(x => x.days === this.range());
    return `نمودار قیمت هر گرم طلای آب‌شدهٔ ۷۵۰ در ${r?.title ?? ''}`;
  });

  ngOnInit() {
    if (this.isBrowser) {
      this.load(this.range());
    }
  }

  select(days: number) {
    if (days === this.range() && this.data()) return;
    this.range.set(days);
    this.hoverIndex.set(null);
    this.tip.set(null);
    this.load(days);
  }

  private load(days: number) {
    const cached = this.cache.get(days);
    if (cached) {
      this.data.set(cached);
      return;
    }

    this.loading.set(true);
    this.api.chart('DAILY', String(days))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          const parsed = this.parse(res?.data);
          if (parsed) this.cache.set(days, parsed);
          // اگر کاربر در این فاصله تب دیگری را زده، پاسخ قدیمی را نشان نده
          if (parsed && this.range() === days) this.data.set(parsed);
          this.loading.set(false);
        },
        error: () => this.loading.set(false)
      });
  }

  private parse(raw: any): ChartData | null {
    if (!raw || !Array.isArray(raw.prices) || !(raw.buyPrice > 0)) return null;

    const now = Number(raw.currentTime) || Date.now();
    const daily: PricePoint[] = raw.prices
      .filter((p: any) => p && p.avg > 0)
      .map((p: any) => ({time: Number(p.time), value: Number(p.avg)}));

    // میانگین روزی که کامل تمام شده (برچسب زمانی هر نقطه، ابتدای روز است)
    const previous = [...daily].reverse().find(p => p.time + DAY <= now);

    // نقطه‌ی امروز میانگینِ نیمه‌کاره است؛ قیمت لحظه‌ای جایش می‌نشیند تا خط به همان عدد بالای کارت برسد
    const points = daily.slice();
    const lastPoint = points[points.length - 1];
    const live = {time: now, value: Number(raw.buyPrice)};
    if (lastPoint && now - lastPoint.time < DAY) {
      points[points.length - 1] = live;
    } else {
      points.push(live);
    }

    return {
      buyPrice: Number(raw.buyPrice),
      sellPrice: Number(raw.sellPrice),
      currentTime: now,
      points,
      previousAverage: previous?.value
    };
  }

  track(event: PointerEvent) {
    const g = this.geometry();
    if (!g) return;
    const svg = event.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();
    if (!rect.width) return;

    const x = (event.clientX - rect.left) / rect.width * W;
    let nearest = 0;
    g.coords.forEach((c, i) => {
      if (Math.abs(c.x - x) < Math.abs(g.coords[nearest].x - x)) nearest = i;
    });

    const left = g.coords[nearest].x / W * rect.width;
    const top = g.coords[nearest].y / H * rect.height;
    // نزدیک لبه‌ها راهنما به داخل کارت می‌چسبد تا بیرون نزند
    const edge = left < 90 ? 'start' : left > rect.width - 90 ? 'end' : 'center';

    this.hoverIndex.set(nearest);
    this.tip.set({left, top, edge});
  }

  untrack() {
    this.hoverIndex.set(null);
    this.tip.set(null);
  }
}
